import { CartDrawerProvider } from '@iheartjane/cart';
import { config } from '@iheartjane/config';
import { initializeApi } from '@iheartjane/data-access';
import { SHADOW_HOST_ID } from '@iheartjane/reefer';
import { useRuntimeConfig } from '@iheartjane/runtime-config';
import {
  initMoengage,
  initTrackJS,
  registerErrorMetadata,
} from '@iheartjane/tracking';
import { useMemo } from 'react';
import type { PropsWithChildren } from 'react';
import { createBrowserRouter, Outlet } from 'react-router';
import { RouterProvider } from 'react-router/dom';

import { Modals } from '@jane/monolith/components/modals';
import { CustomerGlobal } from '@jane/monolith/style/customerGlobal';
import { RethrowRouteError } from '@jane/shared-ecomm/components';
import { initAlgoliaInsights } from '@jane/shared-ecomm/tracking';
import { ScrollRestorationProvider, useEcommApp } from '@jane/shared/providers';
import {
  initCrmMixpanel,
  initMixpanel,
  setPartnerHostedConfig,
} from '@jane/shared/util';

import { buildBoostForAllRoutes } from './app/boost-for-all-routes';
import { useScrollToApp } from './app/hooks/useScrollToApp';
import { buildRoutes } from './app/routes';
import { ShadowRoot } from './shadowRoot';

initMoengage({
  debug: config.moengageDebug,
  env: config.moengageEnv,
  workspaceId: config.moengageWorkspaceId,
});
initMixpanel(config.mixpanelCustomerToken, {
  useLocalStorage: true,
});
initCrmMixpanel(config.mixpanelCrmToken);
initTrackJS({
  application: config.trackJsApplication,
  forwardingDomain: config.trackJsDomain,
  token: config.trackJsToken,
  version: config.appVersion,
});
initAlgoliaInsights();
initializeApi({ baseUrl: config.apiPathBase });

const AppLayout = () => {
  useScrollToApp(SHADOW_HOST_ID);

  return (
    <ShadowRoot>
      <CustomerGlobal />
      <Modals />
      <div style={{ minHeight: '100vh' }}>
        <Outlet />
      </div>
    </ShadowRoot>
  );
};

// Providers that call router hooks, so they must render inside a route element.
const RouterScopedProviders = ({ children }: PropsWithChildren) => (
  <CartDrawerProvider appMode="framelessEmbed">
    <ScrollRestorationProvider isIframe={false}>
      {children}
    </ScrollRestorationProvider>
  </CartDrawerProvider>
);

export const App = () => {
  const { appMode } = useEcommApp();
  registerErrorMetadata('appMode', appMode);
  const { isBoostForAll, isPartnerHosted, partnerHostedPath } =
    useRuntimeConfig();
  setPartnerHostedConfig({ isBoostForAll, isPartnerHosted, partnerHostedPath });

  const router = useMemo(() => {
    return createBrowserRouter(
      [
        {
          element: (
            <RouterScopedProviders>
              <AppLayout />
            </RouterScopedProviders>
          ),
          errorElement: <RethrowRouteError />,
          children: isBoostForAll
            ? buildBoostForAllRoutes()
            : buildRoutes(isPartnerHosted, partnerHostedPath),
        },
      ],
      { basename: isBoostForAll ? partnerHostedPath : undefined }
    );
  }, [isBoostForAll, isPartnerHosted, partnerHostedPath]);

  if (!isPartnerHosted) return <div>Partner Config Not Found!</div>;

  return <RouterProvider router={router} />;
};
